import clsx from 'clsx'
import isNumber from 'lodash/isNumber'
import { QRCodeSVG } from 'qrcode.react'
import React, { useEffect, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { Button, InputNumber, Radio } from '@arco-design/web-react'

import { useQuery } from '@tanstack/react-query'
import {
  AccountControllerCharge,
  AccountControllerGetChargeOrder,
  AccountControllerGetChargeRewardList,
} from '@/apis/v1/accounts'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { CHARGE_CHANNEL, CURRENCY } from '@/constants'
import { useAccountQuery } from '@/pages/app/service'
import { useModel } from '@/store'
import { convertMoney, formatPrice } from '@/utils/format'

export default function ChargeButton(props: {
  amount?: number
  children: React.ReactElement
  onClose?: () => void
}) {
  const { children } = props
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  let [searchParams] = useSearchParams()
  const [amount, setAmount] = React.useState<number>(0)
  const [chargeOrder, setChargeOrder] =
    React.useState<Definitions.CreateChargeOrderOutDto | null>(null)
  const [bonus, setBonus] =
    React.useState<[{ amount: number; reward: number }]>()
  const [phaseStatus, setPhaseStatus] = React.useState<string | undefined>()
  const onClose = () => {
    setIsOpen(false)
    setChargeOrder(null)
    setPhaseStatus(undefined)
    props.onClose?.()
  }
  const [channel, setChannel] = React.useState<CHARGE_CHANNEL>(
    CHARGE_CHANNEL.Alipay,
  )

  const location = useLocation()

  const { showError, showSuccess } = useModel(state => state)
  const [payLoading, setPayLoading] = React.useState(false)
  const { data: accountRes, refetch: accountRefetch } = useAccountQuery()

  const { data: orderRes, ...getChargeOrder } = useQuery({
    queryKey: ['AccountControllerGetChargeOrder'],
    queryFn: () =>
      AccountControllerGetChargeOrder({
        id: chargeOrder?.order?._id,
      }),
    enabled: !!chargeOrder?.order?._id && isOpen,
    refetchInterval: phaseStatus === 'Pending' && isOpen ? 2000 : false,
  })
  useEffect(() => {
    const res = orderRes?.data
    setPhaseStatus(res?.phase)
    if (res?.phase === 'Paid' && phaseStatus !== 'Paid') {
      accountRefetch()
      onClose()
      setPhaseStatus(res?.phase)
      showSuccess('充值成功！')
      setAmount(0)
    }
  }, [orderRes?.data])

  useEffect(() => {
    const channel = searchParams.get('channel')
    const code = searchParams.get('code')
    const message = searchParams.get('message')
    if (channel === CHARGE_CHANNEL.Alipay && code) {
      const isSuccess = code === 'SUCCESS'
      const showToast = isSuccess ? showSuccess : showError
      message && showToast(message)
      history.replaceState({}, '', location.pathname)
      onClose()
    }
  }, [searchParams])

  const { data: getChargeRewardListData, ...getChargeRewardList } = useQuery({
    queryKey: ['AccountControllerGetChargeRewardList'],
    queryFn: () => AccountControllerGetChargeRewardList({}),
    enabled: isOpen,
  })

  useEffect(() => {
    if (getChargeRewardListData?.data) {
      setBonus(getChargeRewardListData.data)
    }
  }, [getChargeRewardListData?.data])

  const handleCreateChargeOrder = async () => {
    setPayLoading(true)
    setPhaseStatus('Pending')
    try {
      const res = await AccountControllerCharge({
        amount: convertMoney(amount || 0),
        channel,
        currency: CURRENCY.CNY,
      })
      if (res.error) {
        setPayLoading(false)
        showError(res?.error || '创建订单失败，请稍候重试或者联系客服')
        return
      }
      setChargeOrder(res.data)
      if (channel === CHARGE_CHANNEL.Alipay && res?.data.result?.code_url) {
        window.open(res?.data.result?.code_url, 'target')
      }
      setTimeout(() => {
        setPayLoading(false)
      }, 1000)
    } catch (error) {
      console.log('error-->', error)
      showError('创建订单失败，请稍候重试或者联系客服')
    } finally {
      setPayLoading(false)
      setPhaseStatus(undefined)
    }
  }

  const matchBonus = (amount: number) => {
    const index = (bonus || []).findIndex((item) => item.amount > amount)
    const matchedItem =
      index === -1 ? bonus?.[bonus?.length - 1] : bonus?.[index - 1]
    return matchedItem?.reward && matchedItem?.reward / 100
  }

  return (
    <>
      {React.cloneElement(children, {
        onClick: () => {
          setIsOpen(true)
        },
      })}
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>充值</DialogTitle>
          </DialogHeader>
          <div>
            <div className="flex flex-col text-lg">
              <div className="flex items-center pb-6">
                <span className="mr-6 text-second">余额</span>
                <span className="text-[24px] font-semibold">
                  {formatPrice(accountRes?.data?.balance)}
                </span>
              </div>
              {bonus && <p className="mb-4 text-second">充值金额</p>}
              <div className="mb-5 grid grid-cols-3 gap-4">
                {(bonus || []).map((item) => (
                  <div className="relative" key={item.amount}>
                    {item.reward && (
                      <span className="absolute left-20 top-1 z-50 flex justify-center items-center whitespace-nowrap rounded-full rounded-bl-none bg-purple-200 px-4 py-[1.5px] text-[12px] h-[22px] text-purple-600">
                        赠 ¥{item.reward / 100}
                      </span>
                    )}
                    <Button
                      className={clsx(
                        'w-full !rounded-md !border-2 !py-10 !text-[24px] flex justify-center items-center',
                        item.amount === amount * 100
                          ? '!border-primary-400 !text-primary-600'
                          : '!border-transparent',
                        '!bg-gray-100 ',
                      )}
                      type="outline"
                      key={item.amount}
                      onClick={() => {
                        setAmount(item.amount / 100)
                      }}
                    >
                      ¥{item.amount / 100}
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex items-center pb-5">
                <div className="w-20 text-lg text-second">输入金额</div>
                <InputNumber
                  prefix="¥"
                  step={1}
                  min={0}
                  max={1000000000}
                  className={clsx('!w-5/12 !border-none !px-2', '!bg-gray-100')}
                  value={amount}
                  formatter={(value, { userTyping, input }) =>
                    userTyping
                      ? input
                      : `${value}`.replace(/B(?=(d{3})+(?!d))/g, ',')
                  }
                  parser={(value) => value.replace(/,/g, '')}
                  onChange={(value) => {
                    setAmount(value)
                  }}
                />
                {amount > 0 && bonus && matchBonus(amount * 100) && (
                  <div>
                    <span className="ml-3 whitespace-nowrap rounded-full rounded-bl-none bg-purple-200 px-2 py-[1.5px] text-[12px] text-purple-600">
                      赠
                    </span>
                    <span className="pl-1 font-semibold">
                      ¥{matchBonus(amount * 100) || 0}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center pb-5">
                <div className="w-20 text-lg text-second">支付方式</div>

                <Radio.Group
                  type="button"
                  name="channel"
                  onChange={(value) => setChannel(value)}
                  value={channel}
                >
                  <Radio value={CHARGE_CHANNEL.Alipay}>支付宝支付</Radio>
                  <Radio value={CHARGE_CHANNEL.WeChat} disabled>
                    微信支付
                  </Radio>
                </Radio.Group>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="primary"
                disabled={!amount || !isNumber(amount) || payLoading}
                className="!h-9 w-full !rounded-full"
                loading={payLoading}
                onClick={handleCreateChargeOrder}
              >
                {payLoading ? '正在请求支付...' : '确认'}
              </Button>
            </DialogFooter>
            {chargeOrder?.result?.code_url &&
              channel === CHARGE_CHANNEL.Alipay &&
              phaseStatus !== 'Paid' && (
                <div className="mt-4 flex flex-col items-center text-xl ">
                  <h2 className="mb-2">{'支付宝'}</h2>
                  <p className="mt-4 text-base text-second ">
                    {'订单号'}：{chargeOrder?.order?._id}
                  </p>
                  <p className="mt-1 text-base text-second ">
                    {'支付状态'}: {phaseStatus}
                  </p>
                </div>
              )}

            {chargeOrder?.result?.code_url &&
              channel === CHARGE_CHANNEL.WeChat &&
              phaseStatus !== 'Paid' && (
                <div className="mt-4 flex flex-col items-center text-xl ">
                  <h2 className="mb-2">{'微信扫码支付'}</h2>
                  <QRCodeSVG
                    value={chargeOrder?.result?.code_url}
                    width={180}
                    height={180}
                  />
                  <p className="mt-4 text-base text-second ">
                    {'订单号'}：{chargeOrder?.order?._id}
                  </p>
                  <p className="mt-1 text-base text-second ">
                    {'支付状态'}: {phaseStatus}
                  </p>
                </div>
              )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
